
import { lazy, Suspense, useEffect, memo } from "react";
import { Helmet } from "react-helmet";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";

// Import components above the fold directly
import { HeroSection } from "@/components/specializations/microsoft365security/HeroSection";
import { ValueMetricsSection } from "@/components/specializations/microsoft365security/ValueMetricsSection";
import { HiddenCapabilitiesSection } from "@/components/specializations/microsoft365security/HiddenCapabilitiesSection";

// Lazy load components below the fold
const LazyPlatformValueSection = lazy(() => 
  import("@/components/specializations/microsoft365security/PlatformValueSection").then(
    module => ({ default: module.PlatformValueSection })
  )
);

const LazyServiceCards = lazy(() => 
  import("@/components/specializations/microsoft365security/ServiceCards").then(
    module => ({ default: module.ServiceCards })
  )
);

const LazyProcessSteps = lazy(() => 
  import("@/components/specializations/microsoft365security/ProcessSteps").then(
    module => ({ default: module.ProcessSteps })
  )
);

const LazyCaseStudies = lazy(() => 
  import("@/components/specializations/microsoft365security/CaseStudies").then(
    module => ({ default: module.CaseStudies })
  )
);

const LazyCtaSection = lazy(() => 
  import("@/components/specializations/microsoft365security/CtaSection").then(
    module => ({ default: module.CtaSection })
  )
);

const LazyGuideSection = lazy(() => 
  import("@/components/specializations/microsoft365security/GuideSection").then(
    module => ({ default: module.GuideSection })
  )
);

// Loading component for better user experience
const SectionLoader = memo(({ height = "py-16" }: { height?: string }) => (
  <div className={`${height} px-4`}>
    <div className="max-w-6xl mx-auto">
      <div className="h-40 bg-gray-100 rounded-xl animate-pulse"></div>
    </div>
  </div>
));

SectionLoader.displayName = "SectionLoader";

/**
 * Microsoft 365 Security Specialization page
 * Features lazy loading of below-the-fold content for improved performance
 * @returns {JSX.Element} The Microsoft365Security page component
 */
const Microsoft365Security = () => {
  const pageTitle = "Microsoft 365 Security Solutions | Maximize Security ROI | edgefi";
  const pageDescription = "Transform your Microsoft 365 environment with specialized security solutions. Maximize existing capabilities and enhance your security posture.";
  const pageUrl = window.location.origin + "/specializations/microsoft-365-security";

  useEffect(() => {
    try {
      // Add Service schema
      const serviceScript = document.createElement('script');
      serviceScript.type = 'application/ld+json';
      serviceScript.innerHTML = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Microsoft 365 Security Solutions",
        "provider": {
          "@type": "Organization",
          "name": "edgefi",
          "url": window.location.origin
        },
        "serviceType": "Microsoft 365 Security",
        "description": "Specialized security services for Microsoft 365 environments that maximize existing capabilities and enhance your overall security posture.",
        "offers": {
          "@type": "Offer",
          "description": "Security assessment, implementation, optimization, and training for Microsoft 365 environments"
        },
        "areaServed": {
          "@type": "Country",
          "name": "United States"
        },
        "url": pageUrl
      });
      document.head.appendChild(serviceScript);
      
      // Add BreadcrumbList schema
      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.innerHTML = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": window.location.origin
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Specializations",
            "item": `${window.location.origin}/specializations`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Microsoft 365 Security",
            "item": pageUrl
          }
        ]
      });
      document.head.appendChild(breadcrumbScript);

      return () => {
        if (document.head.contains(serviceScript)) {
          document.head.removeChild(serviceScript);
        }
        if (document.head.contains(breadcrumbScript)) {
          document.head.removeChild(breadcrumbScript);
        }
      };
    } catch (error) {
      console.error("Error adding schema:", error);
    }
  }, [pageUrl]);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-white">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta name="keywords" content="Microsoft 365 security, M365 security posture, Microsoft Defender, Microsoft security optimization, cloud security, Microsoft compliance" />
          <link rel="canonical" href={pageUrl} />
          
          {/* Geo-targeting meta tags */}
          <meta name="geo.region" content="US" />
          <meta name="geo.position" content="37.09024;-95.712891" />
          <meta name="ICBM" content="37.09024, -95.712891" />
          
          {/* Open Graph tags */}
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={`${window.location.origin}/og-image.png`} />
          <meta property="og:image:alt" content="edgefi Microsoft 365 security specialists" />
          <meta property="og:site_name" content="edgefi" />
          <meta property="og:locale" content="en_US" />
          
          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={`${window.location.origin}/og-image.png`} />
          <meta name="twitter:image:alt" content="edgefi Microsoft 365 security specialists" />
          
          {/* Additional SEO tags */}
          <meta name="author" content="edgefi" />
          <meta name="robots" content="index, follow" />
        </Helmet>
        
        <Navbar />
        
        <main>
          {/* Above the fold content loads immediately */}
          <HeroSection />
          <ValueMetricsSection />
          <HiddenCapabilitiesSection />
          
          {/* Below the fold content loads lazily */}
          <Suspense fallback={<SectionLoader />}>
            <LazyPlatformValueSection />
          </Suspense>
          
          <Suspense fallback={<SectionLoader />}>
            <LazyServiceCards />
          </Suspense>
          
          <Suspense fallback={<SectionLoader />}>
            <LazyProcessSteps />
          </Suspense>
          
          <Suspense fallback={<SectionLoader />}>
            <LazyCaseStudies />
          </Suspense>
          
          <Suspense fallback={<SectionLoader height="py-12" />}>
            <LazyCtaSection />
          </Suspense>
          
          <Suspense fallback={<SectionLoader />}>
            <LazyGuideSection />
          </Suspense>
        </main>
        
        <Footer />
      </div>
    </ErrorBoundary>
  );
};

export default Microsoft365Security;
